import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Badge,
  Form,
  Spinner,
  Card,
  InputGroup,
  Modal,
  Row,
  Col,
  Pagination,
} from "react-bootstrap";
import {
  Search,
  Funnel,
  Eye,
  Pencil,
  ArrowClockwise,
  Envelope,
  Telephone,
  Calendar,
  KeyFill,
} from "react-bootstrap-icons";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://34.206.193.218:4001/api/admin";

/* ================= USER STATUS MAP ================= */
const USER_STATUS_MAP = {
  0: { label: "Unverified", variant: "warning", color: "#ffc107" },
  1: { label: "Active", variant: "success", color: "#198754" },
  2: { label: "Blocked", variant: "danger", color: "#dc3545" },
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [selectedUser, setSelectedUser] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [showView, setShowView] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 10;

  const token =
    localStorage.getItem("adminToken") || localStorage.getItem("token");

  /* ================= FETCH USERS ================= */
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/adminUsers/allUsers`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage,
          limit,
          search: searchTerm || undefined,
          status: filterStatus || undefined,
        },
      });

      if (res.data.success) {
        setUsers(res.data.data || []);
        setTotalPages(res.data.pagination?.totalPages || 1);
        setTotalRecords(res.data.pagination?.totalRecords || 0);
      }
    } catch {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, filterStatus]);

  /* ================= VIEW USER (GET BY ID) ================= */
  const handleViewUser = async (userId) => {
    try {
      setShowView(true);
      setViewLoading(true);
      setViewData(null);

      const res = await axios.get(`${API_BASE}/adminUsers/getById/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.success) {
        setViewData(res.data.data);
      }
    } catch {
      toast.error("Failed to load user details");
    } finally {
      setViewLoading(false);
    }
  };

  /* ================= UPDATE STATUS ================= */
  const updateUserStatus = async (userId, status) => {
    try {
      await axios.put(
        `${API_BASE}/adminUsers/changeStatus/${userId}`,
        { user_status: status },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      toast.success("User status updated");
      fetchUsers();
    } catch {
      toast.error("Failed to update status");
    }
  };

  /* ================= TOGGLE ACTIVE/BLOCK ================= */
  const toggleActiveBlock = (user) => {
    // Only toggle between Active (1) and Blocked (2)
    const newStatus = user.user_status === 1 ? 2 : 1;
    updateUserStatus(user._id, newStatus);
  };

  /* ================= UPDATE USER (EDIT) ================= */
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      setUpdating(true);

      // Prepare data for update - include all fields except email
      const updateData = {
        name: selectedUser.name,
        number: selectedUser.number,
        user_status: selectedUser.user_status,
      };

      const formData = new FormData();

      // Append text fields
      Object.keys(updateData).forEach((key) => {
        formData.append(key, updateData[key]);
      });

      if (selectedUser.newImage) {
        formData.append("files", selectedUser.newImage);
      }

      await axios.put(
        `${API_BASE}/adminUsers/updateUser/${selectedUser._id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("User updated successfully");
      setShowEdit(false);
      fetchUsers();
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update user");
    } finally {
      setUpdating(false);
    }
  };

  /* ================= CHANGE PASSWORD ================= */
  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      setChangingPassword(true);

      const res = await axios.put(
        `${API_BASE}/adminUsers/resetPassword/${selectedUser._id}`,
        {
          password: selectedUser.newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data.success) {
        toast.success("Password reset successfully! Email sent to user.");
        setShowChangePassword(false);
        setSelectedUser(null);
      }
    } catch (error) {
      toast.error("Failed to reset password");
      console.error("Password reset error:", error);
    } finally {
      setChangingPassword(false);
    }
  };

  /* ================= HELPERS ================= */
  const getInitials = (name) =>
    name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const renderPagination = () => {
    const items = [];
    for (let i = 1; i <= totalPages; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return items;
  };

  return (
    <div style={{ backgroundColor: "#ffffffff", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER + FILTERS (ONE ROW) */}
      <Card className="border-0 mb-4">
        <Card.Body className="p-0">
          <Row className="align-items-center g-3">
            {/* TITLE */}
            <Col md={4}>
              <h4 className="fw-bold mb-0">User Management</h4>
              <small className="text-muted">Manage application users</small>
            </Col>

            {/* SEARCH */}
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <Search />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search by name or email"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </InputGroup>
            </Col>

            {/* STATUS FILTER */}
            <Col md={3}>
              <InputGroup>
                <InputGroup.Text>
                  <Funnel />
                </InputGroup.Text>
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All Status</option>
                  <option value="0">Unverified</option>
                  <option value="1">Active</option>
                  <option value="2">Blocked</option>
                </Form.Select>
              </InputGroup>
            </Col>

            {/* REFRESH ICON ONLY */}
            <Col md={1} className="text-end">
              <Button
                variant="outline-primary"
                onClick={fetchUsers}
                title="Refresh"
              >
                <ArrowClockwise />
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* TABLE */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user._id}>
                        <td>
                          <div className="d-flex align-items-center">
                            {user.profilePicture ? (
                              <img
                                src={user.profilePicture}
                                alt=""
                                width={36}
                                height={36}
                                className="rounded-circle me-2"
                              />
                            ) : (
                              <div
                                className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-2"
                                style={{ width: 36, height: 36 }}
                              >
                                {getInitials(user.name)}
                              </div>
                            )}
                            {user.name}
                          </div>
                        </td>

                        <td>
                          <Envelope className="me-1" /> {user.email}
                        </td>
                        <td>
                          <Telephone className="me-1" /> {user.number}
                        </td>

                        <td>
                          <Badge
                            bg={USER_STATUS_MAP[user.user_status]?.variant}
                            className="px-3 py-2 fw-normal"
                            style={{
                              backgroundColor:
                                USER_STATUS_MAP[user.user_status]?.color,
                              border: "none",
                            }}
                          >
                            {USER_STATUS_MAP[user.user_status]?.label}
                          </Badge>
                        </td>

                        <td>
                          <div className="d-flex align-items-center gap-2">
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleViewUser(user._id)}
                            title="View Details"
                          >
                            <Eye />
                          </Button>

                          <Button
                            size="sm"
                            variant="warning"
                            onClick={() => {
                              setSelectedUser(user);
                              setShowEdit(true);
                            }}
                            title="Edit User"
                          >
                            <Pencil />
                          </Button>

                          <Button
                            size="sm"
                            variant="info"
                            onClick={() => {
                              setSelectedUser(user);
                              setShowChangePassword(true);
                            }}
                            title="Change Password"
                          >
                            <KeyFill />
                          </Button>

                          {/* Toggle Active/Block Button - Only shown for Active(1) or Blocked(2) users */}
                          {/* MODERN STATUS TOGGLE */}
                          {user.user_status !== 0 && (
                            <div className="d-flex align-items-center gap-2">
                              <Form.Check
                                type="switch"
                                id={`status-switch-${user._id}`}
                                checked={user.user_status === 1}
                                onChange={() => toggleActiveBlock(user)}
                                className={`status-switch ${
                                  user.user_status === 1
                                    ? "switch-active"
                                    : "switch-blocked"
                                }`}
                              />
                              <small
                                className={`fw-semibold ${
                                  user.user_status === 1
                                    ? "text-success"
                                    : "text-danger"
                                }`}
                              >
                                {user.user_status === 1 ? "Active" : "Blocked"}
                              </small>
                            </div>
                          )}

                          {/* For Unverified users, show Activate button */}
                          {user.user_status === 0 && (
                            <Button
                              size="sm"
                              variant="success"
                              onClick={() => updateUserStatus(user._id, 1)}
                              title="Activate User"
                            >
                              Activate
                            </Button>
                          )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="p-3 d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  Showing {(currentPage - 1) * limit + 1}–
                  {Math.min(currentPage * limit, totalRecords)} of{" "}
                  {totalRecords}
                </small>
                <Pagination className="mb-0">{renderPagination()}</Pagination>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* ================= VIEW MODAL ================= */}
      <Modal
        show={showView}
        onHide={() => setShowView(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>User Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewLoading ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : (
            viewData && (
              <Row className="g-4">
                <Col md={4} className="text-center">
                  <img
                    src={viewData.user.profilePicture}
                    alt=""
                    className="rounded-circle mb-3"
                    width={120}
                    height={120}
                  />
                  <h5>{viewData.user.name}</h5>
                  <Badge
                    bg={USER_STATUS_MAP[viewData.user.user_status]?.variant}
                    className="px-3 py-2"
                    style={{
                      backgroundColor:
                        USER_STATUS_MAP[viewData.user.user_status]?.color,
                      border: "none",
                    }}
                  >
                    {USER_STATUS_MAP[viewData.user.user_status]?.label}
                  </Badge>
                </Col>

                <Col md={8}>
                  <p>
                    <Envelope className="me-2" /> {viewData.user.email}
                  </p>
                  <p>
                    <Telephone className="me-2" /> {viewData.user.number}
                  </p>
                  <p>
                    <Calendar className="me-2" />
                    Created:{" "}
                    {new Date(viewData.user.createdAt).toLocaleDateString()}
                  </p>
                  <p>
                    <Calendar className="me-2" />
                    Updated:{" "}
                    {new Date(viewData.user.updatedAt).toLocaleDateString()}
                  </p>

                  <Card className="mt-3">
                    <Card.Body>
                      <strong>Active Projects:</strong>{" "}
                      {viewData.activeProjects.length}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )
          )}
        </Modal.Body>
      </Modal>

      {/* ================= EDIT MODAL ================= */}
      <Modal show={showEdit} onHide={() => setShowEdit(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <Form onSubmit={handleUpdateUser}>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  value={selectedUser.name}
                  onChange={(e) =>
                    setSelectedUser({ ...selectedUser, name: e.target.value })
                  }
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  value={selectedUser.email}
                  disabled
                  readOnly
                  className="bg-light"
                />
                <Form.Text className="text-muted">
                  Email cannot be changed
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  value={selectedUser.number || ""}
                  maxLength={13}
                  onChange={(e) =>
                    setSelectedUser({ ...selectedUser, number: e.target.value })
                  }
                />
              </Form.Group>

              {/* <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={selectedUser.user_status}
                  onChange={(e) =>
                    setSelectedUser({ 
                      ...selectedUser, 
                      user_status: parseInt(e.target.value) 
                    })
                  }
                >
                  <option value={0}>Unverified</option>
                  <option value={1}>Active</option>
                  <option value={2}>Blocked</option>
                </Form.Select>
              </Form.Group> */}

              <Form.Group className="mb-4">
                <Form.Label>Profile Image</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    setSelectedUser({
                      ...selectedUser,
                      newImage: e.target.files[0],
                    })
                  }
                />
                <Form.Text className="text-muted">
                  Leave empty to keep current image
                </Form.Text>
              </Form.Group>

              <div className="d-flex gap-2">
                <Button
                  variant="secondary"
                  className="w-100"
                  onClick={() => setShowEdit(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="w-100" disabled={updating}>
                  {updating ? <Spinner size="sm" /> : "Update User"}
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>

      {/* ================= CHANGE PASSWORD MODAL ================= */}
      <Modal
        show={showChangePassword}
        onHide={() => setShowChangePassword(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <KeyFill className="me-2" />
            Change Password for {selectedUser?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <Form onSubmit={handleChangePassword}>
              <div className="alert alert-info mb-3">
                <small>
                  <strong>Note:</strong> This will reset the user's password and
                  send them an email with the new password.
                </small>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  value={selectedUser.email}
                  disabled
                  readOnly
                  className="bg-light"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>New Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Enter new password"
                  value={selectedUser.newPassword || ""}
                  onChange={(e) =>
                    setSelectedUser({
                      ...selectedUser,
                      newPassword: e.target.value,
                    })
                  }
                  required
                  minLength={6}
                />
                <Form.Text className="text-muted">
                  Minimum 6 characters. Use a mix of letters, numbers, and
                  symbols.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Confirm New Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Confirm new password"
                  value={selectedUser.confirmPassword || ""}
                  onChange={(e) =>
                    setSelectedUser({
                      ...selectedUser,
                      confirmPassword: e.target.value,
                    })
                  }
                  required
                />
                {selectedUser.newPassword &&
                  selectedUser.confirmPassword &&
                  selectedUser.newPassword !== selectedUser.confirmPassword && (
                    <Form.Text className="text-danger">
                      Passwords do not match
                    </Form.Text>
                  )}
              </Form.Group>

              <div className="d-flex gap-2">
                <Button
                  variant="secondary"
                  className="w-100"
                  onClick={() => {
                    setShowChangePassword(false);
                    setSelectedUser(null);
                  }}
                  disabled={changingPassword}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="w-100"
                  disabled={
                    changingPassword ||
                    !selectedUser.newPassword ||
                    !selectedUser.confirmPassword ||
                    selectedUser.newPassword !== selectedUser.confirmPassword
                  }
                  variant="primary"
                >
                  {changingPassword ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Resetting Password...
                    </>
                  ) : (
                    <>
                      <KeyFill className="me-2" />
                      Reset Password
                    </>
                  )}
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Users;
